(() => {
  if (window.hmProductStickyInitialized) return;
  window.hmProductStickyInitialized = true;

  const controllers = new Set();
  const roots = new WeakMap();
  let heightFrame;

  const reserveHeight = () => {
    if (heightFrame) return;
    heightFrame = window.requestAnimationFrame(() => {
      heightFrame = null;
      const height = Math.ceil(Math.max(0, ...Array.from(controllers, (controller) => {
        const bar = controller.root.querySelector('[data-hm-compact-sticky]');
        return bar?.isConnected ? bar.getBoundingClientRect().height : 0;
      })));
      const value = `${height}px`;
      if (document.documentElement.style.getPropertyValue('--hm-sticky-height') !== value) {
        document.documentElement.style.setProperty('--hm-sticky-height', value);
      }
    });
  };

  const primaryButton = (root) => root.querySelector('.product-form--buy-buttons button[name="add"]');
  const setAttribute = (element, name, value) => {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const toggleAttribute = (element, name, value) => {
    if (element.hasAttribute(name) !== value) element.toggleAttribute(name, value);
  };
  const toggleClass = (element, name, value) => {
    if (element.classList.contains(name) !== value) element.classList.toggle(name, value);
  };

  const initialize = (root) => {
    if (roots.has(root) || !root.querySelector('[data-hm-compact-sticky]')) return;

    const controller = { root, frame: null, bar: null };
    const sync = () => {
      controller.frame = null;
      if (!root.isConnected) return destroy(controller);

      const bar = root.querySelector('[data-hm-compact-sticky]');
      if (bar !== controller.bar) {
        if (controller.bar) controller.resizeObserver?.unobserve(controller.bar);
        controller.bar = bar;
        if (bar) controller.resizeObserver?.observe(bar);
      }

      const button = bar?.querySelector('[data-hm-sticky-add]');
      const primary = primaryButton(root);
      if (button) {
        const disabled = !primary || primary.disabled;
        const busy = primary?.getAttribute('aria-disabled') === 'true' || primary?.classList.contains('loading') || false;
        toggleAttribute(button, 'disabled', disabled);
        setAttribute(button, 'aria-disabled', String(disabled || busy));
        toggleClass(button, 'loading', busy);

        const spinner = button.querySelector('.loading__spinner');
        if (spinner) toggleClass(spinner, 'hidden', !busy);

        const label = button.querySelector('[data-hm-sticky-label]');
        let primaryLabel = primary?.querySelector('span')?.textContent.trim();
        // Some existing templates include the unit price in their native CTA.
        // The compact bar already has its own price, so omit only that exact suffix.
        if (primaryLabel && primary.closest('form')?.dataset.showPrice === 'true') {
          const price = primary.closest('.product__info-wrapper')?.querySelector('.price__container .price-item--last')?.textContent.trim();
          const suffix = price ? ` - ${price}` : '';
          if (suffix && primaryLabel.endsWith(suffix)) primaryLabel = primaryLabel.slice(0, -suffix.length);
        }
        if (label && primaryLabel && label.textContent.trim() !== primaryLabel) label.textContent = primaryLabel;
      }
      reserveHeight();
    };

    controller.schedule = () => {
      if (!controller.frame) controller.frame = window.requestAnimationFrame(sync);
    };
    controller.observer = new MutationObserver(controller.schedule);
    controller.observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden'],
    });
    if ('ResizeObserver' in window) controller.resizeObserver = new ResizeObserver(reserveHeight);
    roots.set(root, controller);
    controllers.add(controller);
    sync();
  };

  const destroy = (controller) => {
    controller.observer.disconnect();
    controller.resizeObserver?.disconnect();
    if (controller.frame) window.cancelAnimationFrame(controller.frame);
    controllers.delete(controller);
    roots.delete(controller.root);
    reserveHeight();
  };
  const initializeAll = () => document.querySelectorAll('product-info.hm-product-page').forEach(initialize);

  // product-info replaces the bar contents when a variant changes. Delegation
  // forwards each sticky click to the current native submit button exactly once;
  // the existing Shopify product-form still owns validation, quantity and cart.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-hm-sticky-add]');
    if (!button || !button.closest('[data-hm-compact-sticky]')) return;
    const root = button.closest('product-info.hm-product-page');
    if (!root) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const primary = primaryButton(root);
    if (!primary || primary.disabled || primary.getAttribute('aria-disabled') === 'true') return;
    primary.click();
    roots.get(root)?.schedule();
  }, true);

  window.addEventListener('resize', reserveHeight, { passive: true });
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('product-info:loaded', initializeAll);
  document.addEventListener('shopify:section:unload', (event) => {
    Array.from(controllers).forEach((controller) => {
      if (event.target.contains(controller.root)) destroy(controller);
    });
  });
  if (document.fonts?.ready) document.fonts.ready.then(reserveHeight);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
})();
